import React, { useState } from 'react';
import RequestRepair from '../components/RequestRepair/RequestRepair';
import OrderReceived from '../components/OrderReceived/OrderReceived';
import FloatingButton from '../components/FloatingButton/FloatingButton';

export default function TermsOfService() {
  const [showRepair, setShowRepair] = useState(false);
  const [showOrderReceived, setShowOrderReceived] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const handleRepairSubmit = (submittedOrderId) => {
    setOrderId(submittedOrderId);
    setShowRepair(false);
    setShowOrderReceived(true);
  };

  return (
    <>
      <div className="policy-page-wrapper">
        {/* Background Ambient Elements */}
        <div className="policy-bg-glow" aria-hidden="true" />
        <div className="policy-bg-dots-left" aria-hidden="true" />
        <div className="policy-bg-dots-right" aria-hidden="true" />

        <div className="policy-container">
          <header className="policy-header">
            <div className="policy-badge">
              <span className="star-icon">★</span>
              <span>TERMS &amp; CONDITIONS</span>
            </div>

            <h1>
              Terms of <span className="gold-text">Service</span>
            </h1>

            {/* Gold Diamond Divider */}
            <div className="heading-divider" aria-hidden="true">
              <div className="divider-line" />
              <div className="divider-diamond" />
              <div className="divider-line" />
            </div>

            <p className="subnote">Independent Repair Service • Not affiliated with any brand</p>
            <p className="effective-date">Effective Date: 07/08/2026</p>
          </header>

          <div className="policy-content">
            <p className="intro">
              Welcome to DoorStep Maintenance &amp; Repair (DSMR). By using our services, accessing our website, or scheduling a doorstep appointment, you agree to these Terms of Service ("Terms"). Please read them carefully. These Terms establish a legally binding agreement between you ("Customer," "You") and DoorStep Maintenance &amp; Repair ("Company," "DSMR," "We," "Us," or "Our").
            </p>

            <p className="intro">
              By booking or accepting any repair service, you confirm that you have read, understood, and agreed to these Terms alongside our Privacy Policy, Disclaimer, Refund &amp; Cancellation Policy, and Warranty Policy. If you do not agree, please do not use our services.
            </p>

            <section className="policy-section">
              <h2>1. Scope of Services</h2>
              <p>We provide professional, independent doorstep and service-center repair solutions for:</p>
              <ul>
                <li>Smartphones (Android, iPhone, foldable devices)</li>
                <li>Tablets &amp; iPads</li>
                <li>Laptops (Windows, MacBook, Chromebook)</li>
                <li>Desktops &amp; All-in-One PCs</li>
                <li>Software troubleshooting, OS installation &amp; malware removal</li>
                <li>Hardware replacement (displays, batteries, charging ports, motherboards)</li>
                <li>Data recovery and device health assessments</li>
              </ul>
              <p>Our services encompass on-site diagnostics, minor component repairs, part replacements, and service-center pick-up/drop services when complex equipment is required.</p>
            </section>

            <section className="policy-section">
              <h2>2. Free Home Visit &amp; Service Fee Policy</h2>
              <ul>
                <li><strong>Free Doorstep Visit:</strong> Doorstep inspection and visit fees are 100% free, provided the customer proceeds with the recommended repair service. The customer pays only for the repair labor and replacement parts.</li>
                <li><strong>Technician Compensation Fee:</strong> If the technician has explained the estimated repair costs, parts, and terms over phone/WhatsApp prior to the visit, and the customer confirms the appointment but subsequently denies the repair upon technician arrival, a ₹599 technician compensation fee will apply to cover travel, time, and on-site assessment.</li>
                <li><strong>Service Center Pickup:</strong> Where doorstep repair is technically infeasible, pick-up and delivery to our central facility remain free if the repair is authorized and completed.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>3. Estimates, Charges &amp; Payments</h2>
              <ul>
                <li>Estimates provided online or over phone are initial assessments. Final costs are confirmed after hands-on diagnosis. Additional issues discovered during repair will be communicated and require customer approval before proceeding.</li>
                <li>Payment is due immediately upon completion of the repair and customer testing of the device.</li>
                <li>Accepted payment methods include UPI (Google Pay, PhonePe, Paytm), Cash, Credit/Debit Cards, and Net Banking.</li>
                <li><strong>Bill / Invoice Issuance:</strong> Providing the official bill/invoice is completely the responsibility of DSMR Solutions. Digital bills are issued directly by DSMR Solutions via WhatsApp, SMS, or Email within <strong>24-48 hours</strong> after full payment is received.</li>
                <li><strong>Technician Role Regarding Bills:</strong> Technicians are not authorized to issue, generate, or alter official bills/invoices. Customers must contact DSMR Solutions directly for any bill or receipt requests and should not ask doorstep technicians for bills.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>4. Warranty Terms</h2>
              <ul>
                <li>All eligible repairs and replacement parts include limited warranty coverage ranging from Testing Warranty up to 12 months, as specified on your invoice.</li>
                <li>Warranty strictly covers the specific component replaced or issue repaired during the original service.</li>
                <li><strong>Claim Processing:</strong>
                  <ul>
                    <li>The 1st warranty claim for a covered repair is completely free of charge.</li>
                    <li>For any 2nd or subsequent warranty claim on the same repair, a ₹599 service/inspection charge applies.</li>
                    <li>Doorstep warranty visits may take up to 7 business days to schedule depending on location. Instant warranty service is available at our store/service center.</li>
                  </ul>
                </li>
                <li><strong>Void Criteria:</strong> Warranty is automatically voided if the device suffers physical drop damage, green screen lines, liquid exposure, third-party tampering, software modifications, or battery swelling due to non-standard charging. Refer to our <a href="/warranty-policy" style={{ color: '#C99E54', textDecoration: 'underline' }}>Warranty Policy</a> for full details.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>5. Appointment, Cancellation &amp; Rescheduling</h2>
              <ul>
                <li>Appointments can be booked via our website, phone call, or WhatsApp.</li>
                <li><strong>Cancellation Window:</strong> Free cancellation or rescheduling is permitted at least 4 hours prior to the scheduled appointment time.</li>
                <li>Cancellations requested after technician dispatch or non-availability at the location will incur the ₹599 technician compensation fee (see <a href="/refund-policy" style={{ color: '#C99E54', textDecoration: 'underline' }}>Refund &amp; Cancellation Policy</a>).</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>6. Technician Safety &amp; Work Environment Guidelines</h2>
              <p>DSMR Solutions is committed to ensuring a safe, respectful, and secure environment for our doorstep service technicians:</p>
              <ul>
                <li><strong>Safe Workspace:</strong> Customers must provide a safe, well-lit, and clean working area equipped with access to a electrical outlet, flat surface, and adequate seating for the repair.</li>
                <li><strong>Respectful Conduct &amp; Zero Tolerance:</strong> DSMR maintains a strict zero-tolerance policy towards verbal abuse, physical harassment, intimidation, discrimination, or threat of harm towards our technicians.</li>
                <li><strong>Technician Safety Protocols:</strong> Technicians reserve the right to immediately pause or cancel any repair service and leave the premises if they feel unsafe, encounter hostile behavior, or if the work environment poses health/safety hazards.</li>
                <li><strong>Applicable Fees:</strong> In the event a service is terminated due to customer non-compliance with safety or respectful conduct guidelines, the standard ₹599 technician compensation fee will remain payable.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>7. Data Protection &amp; Customer Responsibilities</h2>
              <ul>
                <li>Customers are required to remove SIM cards, memory cards, and protective cases prior to handing over the device, and back up all personal data.</li>
                <li>DSMR is not liable for data loss or corruption during hardware diagnostics, OS reinstallation, or data recovery operations.</li>
                <li>Customers must test device functionality in the technician's presence before confirming completion and making final payment.</li>
                <li><strong>Bill Verification &amp; Channel:</strong> It is the sole responsibility of the customer to check and verify all details listed on the bill/invoice upon receipt from DSMR Solutions. Any bill inquiries or discrepancy reports must be directed to DSMR Solutions customer support (+91.8700319769 / support@dsmrsolutions.in). Customers cannot request or demand bills directly from technicians.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>8. Unclaimed &amp; Abandoned Devices</h2>
              <ul>
                <li>For devices collected for service-center repair, customers will be notified immediately upon completion or diagnostic outcome.</li>
                <li>Devices uncollected or unpaid for more than 30 days following notice will be considered abandoned. DSMR reserves the right to dispose of, recycle, or sell abandoned devices to cover unrecovered repair costs.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>8. Software &amp; Licensing</h2>
              <ul>
                <li>DSMR installs only genuine, open-source, or customer-licensed software. Customers providing software installers are responsible for license validity.</li>
                <li>We do not support or install unauthorized pirated software.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>9. Limitations of Liability &amp; Force Majeure</h2>
              <ul>
                <li>DSMR is not liable for indirect, incidental, or consequential losses (such as loss of income, data, or business opportunities).</li>
                <li>Total liability for any validated claim is limited strictly to a refund of the amount paid for the specific service.</li>
                <li>We are not responsible for service delays caused by events beyond our control, including extreme weather, traffic gridlock, strikes, or supply chain component delays (Force Majeure).</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>10. Governing Law &amp; Dispute Resolution</h2>
              <ul>
                <li>These Terms are governed by and construed in accordance with the laws of India.</li>
                <li>Any disputes shall be subject to the exclusive jurisdiction of the competent courts in New Delhi, India.</li>
                <li>Customers agree to contact DSMR customer support for amicable resolution prior to initiating legal or regulatory proceedings.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>11. Modifications to Terms</h2>
              <p>We reserve the right to modify these Terms at any time. Updated versions will be posted on this page with an updated "Effective Date." Continued use of our services constitutes acceptance of updated Terms.</p>
            </section>

            <section className="policy-section">
              <h2>12. Contact Us</h2>
              <p>For questions or formal notices concerning these Terms of Service, please reach out:</p>
              <div className="contact-info">
                <p><strong>DSMR Solutions</strong></p>
                <p>Website: dsmrsolutions.in</p>
                <p>Phone: +91.8700319769</p>
                <p>Email: support@dsmrsolutions.in</p>
              </div>
            </section>
          </div>
        </div>

        {showRepair && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <RequestRepair
              isModal={true}
              onClose={() => setShowRepair(false)}
              onSubmit={handleRepairSubmit}
            />
          </div>
        )}

        {showOrderReceived && (
          <OrderReceived
            orderId={orderId}
            onClose={() => setShowOrderReceived(false)}
          />
        )}



        <style>{`
          .policy-page-wrapper {
            min-height: 100vh;
            background: linear-gradient(135deg, #f9f4ee 0%, #fbf6f1 35%, #fdf8f5 70%, #fdf9f4 100%);
            padding: 8rem 1.5rem 4rem 1.5rem;
            position: relative;
            overflow: hidden;
            font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }

          .policy-bg-glow {
            position: absolute;
            top: -20px;
            left: 50%;
            transform: translateX(-50%);
            width: 900px;
            height: 480px;
            background: radial-gradient(ellipse at center, rgba(235, 205, 155, 0.35) 0%, rgba(250, 246, 240, 0) 70%);
            pointer-events: none;
            z-index: 0;
          }

          .policy-bg-dots-left {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 300px;
            background-image: radial-gradient(#C99E54 1.4px, transparent 1.4px);
            background-size: 22px 22px;
            opacity: 0.25;
            mask-image: linear-gradient(to right, rgba(0,0,0,0.8), transparent);
            -webkit-mask-image: linear-gradient(to right, rgba(0,0,0,0.8), transparent);
            pointer-events: none;
          }

          .policy-bg-dots-right {
            position: absolute;
            top: 0;
            bottom: 0;
            right: 0;
            width: 300px;
            background-image: radial-gradient(#C99E54 1.4px, transparent 1.4px);
            background-size: 22px 22px;
            opacity: 0.25;
            mask-image: linear-gradient(to left, rgba(0,0,0,0.8), transparent);
            -webkit-mask-image: linear-gradient(to left, rgba(0,0,0,0.8), transparent);
            pointer-events: none;
          }

          .policy-container {
            max-width: 860px;
            margin: 0 auto;
            background: linear-gradient(135deg, #fefaf4 0%, #fdf9f3 35%, #fefaf6 70%, #fdf9f6 100%);
            border-radius: 24px;
            border: 1px solid rgba(201, 158, 84, 0.25);
            box-shadow: inset 0 1px 2px rgba(255,255,255,.95), 0 0 0 1px rgba(255,233,194,.45), 0 10px 24px rgba(191,160,110,.10), 0 14px 35px rgba(180,150,100,.09), 0 50px 100px rgba(180,150,100,.06);
            overflow: hidden;
            position: relative;
            z-index: 1;
          }

          .policy-header {
            background: linear-gradient(180deg, #FFFFFF 0%, #FAF5EE 100%);
            color: #0F172A;
            padding: 3rem 2.5rem 2rem 2.5rem;
            text-align: center;
            border-bottom: 1px solid rgba(201, 158, 84, 0.2);
          }

          .policy-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 1.5px;
            color: #C99E54;
            background: rgba(201, 158, 84, 0.08);
            border: 1px solid rgba(201, 158, 84, 0.3);
            border-radius: 30px;
            padding: 6px 16px;
            margin-bottom: 1rem;
            text-transform: uppercase;
          }

          .star-icon {
            font-size: 0.85rem;
            color: #F0B040;
          }

          .policy-header h1 {
            font-size: 2.75rem;
            font-weight: 800;
            color: #0F172A;
            margin: 0 0 0.5rem 0;
            letter-spacing: -0.5px;
          }

          .gold-text {
            color: #C98E32;
            background: linear-gradient(135deg, #D99215 0%, #B88228 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .heading-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin: 1rem 0 1.25rem 0;
          }

          .divider-line {
            height: 1px;
            width: 60px;
            background: linear-gradient(90deg, transparent, #C99E54, transparent);
          }

          .divider-diamond {
            width: 7px;
            height: 7px;
            background: #C99E54;
            transform: rotate(45deg);
          }

          .subnote {
            margin: 0 0 0.4rem 0;
            color: #64748B;
            font-size: 0.98rem;
            font-weight: 500;
          }

          .effective-date {
            font-size: 0.95rem;
            color: #94A3B8;
            margin: 0;
            font-weight: 500;
          }

          .policy-content {
            padding: 3rem 2.5rem;
            line-height: 1.8;
            color: #334155;
          }

          .intro {
            font-size: 1.08rem;
            margin-bottom: 1.5rem;
            padding: 1.5rem;
            background: rgba(201, 158, 84, 0.06);
            border-left: 4px solid #C99E54;
            border-radius: 0 12px 12px 0;
            color: #1E293B;
          }

          .policy-section {
            margin-bottom: 2.75rem;
          }

          .policy-section h2 {
            color: #0F172A;
            font-size: 1.45rem;
            font-weight: 700;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid rgba(201, 158, 84, 0.18);
            display: flex;
            align-items: center;
          }

          .policy-section p {
            margin-bottom: 1rem;
            font-size: 1.02rem;
            color: #334155;
          }

          .policy-section ul,
          .policy-section ol {
            margin: 1rem 0 1.25rem 0;
            padding-left: 1.6rem;
          }

          .policy-section li {
            margin-bottom: 0.6rem;
            font-size: 1.02rem;
            color: #334155;
          }

          .policy-section ul ul {
            margin: 0.5rem 0;
            padding-left: 1.5rem;
          }

          .contact-info {
            background: linear-gradient(135deg, #FFFDF9 0%, #FDF8F0 100%);
            padding: 1.75rem;
            border-radius: 14px;
            border: 1px solid rgba(201, 158, 84, 0.25);
            box-shadow: 0 4px 15px rgba(201, 158, 84, 0.06);
            margin-top: 1.25rem;
          }

          .contact-info p {
            margin: 0.5rem 0;
            font-size: 1.02rem;
          }

          .contact-info strong {
            color: #0F172A;
          }

          @media (max-width: 768px) {
            .policy-page-wrapper {
              padding: 6.5rem 1rem 2rem 1rem;
            }

            .policy-container {
              border-radius: 16px;
            }

            .policy-header {
              padding: 2.25rem 1.5rem 1.5rem 1.5rem;
            }

            .policy-header h1 {
              font-size: 2.1rem;
            }

            .policy-content {
              padding: 2rem 1.5rem;
            }

            .intro {
              font-size: 1rem;
              padding: 1.15rem;
            }

            .policy-section h2 {
              font-size: 1.3rem;
            }

            .policy-section p,
            .policy-section li {
              font-size: 0.98rem;
            }
          }

          @media (max-width: 480px) {
            .policy-page-wrapper {
              padding: 5.5rem 0.5rem 1.5rem 0.5rem;
            }

            .policy-header {
              padding: 1.75rem 1rem 1.25rem 1rem;
            }

            .policy-header h1 {
              font-size: 1.85rem;
            }

            .policy-content {
              padding: 1.5rem 1rem;
            }
          }
        `}</style>
      </div>
    </>
  );
}
